import { InlineIcon } from "@iconify/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Loader } from "../../../../components/widgets/Loader";
import { brand } from "../../../../context";
import { useApoAuth } from "../../../../hooks";
import { updateSubAgent, uploadImage } from "../api";

type LogoPickerModalProps = {
  onClose: () => void;
  logo_url?: string;
  appUserDataQueryKey: (string | undefined)[];
};

export function LogoPickerModal({ onClose, logo_url, appUserDataQueryKey }: LogoPickerModalProps) {
  const [logoPreview, setLogoPreview] = useState<string | undefined>(logo_url);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { appUserData, getIdTokenClaims } = useApoAuth();
  const queryClient = useQueryClient();

  // Reset preview when logo_url changes
  useEffect(() => {
    setLogoPreview(logo_url);
    setSelectedFile(null);
  }, [logo_url]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (e.g., max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Mutation to upload image and update subagent
  const uploadAndUpdateMutation = useMutation({
    mutationFn: async (file: File) => {
      // Get authentication token
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims || !("__raw" in tokenClaims)) {
        throw new Error("Unable to get authentication token");
      }
      const token = (tokenClaims as { __raw: string }).__raw;

      // Get subagent_id from appUserData
      if (!appUserData?.entity_id || appUserData.entity_type !== "subagent") {
        throw new Error("User is not associated with a subagent");
      }
      const subagentId = appUserData.entity_id;

      // Step 1: Upload image to Cloudflare
      const formData = new FormData();
      formData.append("image", file);
      const uploadResponse = await uploadImage(formData);

      if (!uploadResponse.data?.url) {
        throw new Error("Failed to upload image");
      }

      // Step 2: Update subagent with the new logo URL
      await updateSubAgent(
        {
          subagent_id: subagentId,
          logo_url: uploadResponse.data.url,
        },
        token,
      );

      return uploadResponse.data.url;
    },
    onSuccess: (logoUrl) => {
      toast.success("Logo updated successfully");
      setLogoPreview(logoUrl);
      setSelectedFile(null);
      // Invalidate the app user query to refetch updated data
      queryClient.invalidateQueries({ queryKey: appUserDataQueryKey });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update logo");
    },
  });

  const handleSave = () => {
    if (!selectedFile) {
      // If no file selected, just close the modal
      onClose();
      return;
    }

    uploadAndUpdateMutation.mutate(selectedFile);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className="Modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
        }
      }}
    >
      <div
        className="Modal__card LogoPickerModal__card max-w-[600px]"
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="Modal__card__header">
          <div>Edit Logo</div>
          <InlineIcon icon="mdi:close" className="Modal__card__header__icon" onClick={onClose} />
        </div>
        <div className="Modal__card__body" style={{ position: "relative" }}>
          {uploadAndUpdateMutation.isLoading && <Loader size="lg" />}
          <div className="grid gap-6">
            {/* Logo Preview */}
            <div className="grid gap-3">
              <div className="text-base font-medium">Logo Preview</div>
              <div className="grid justify-items-center py-4 border-2 border-dashed border-gray-300 rounded-sm bg-gray-50">
                {logoPreview ? (
                  <div className="grid gap-3 items-center">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-w-[300px] max-h-[200px] object-contain"
                    />
                    {selectedFile && (
                      <div className="text-sm text-gray-600 text-center">
                        {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid gap-2 items-center text-center text-gray-500">
                    <InlineIcon icon="mdi:image-outline" className="text-4xl" />
                    <div>No logo selected</div>
                  </div>
                )}
              </div>
            </div>

            {/* File Input */}
            <div className="grid gap-3">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/jpeg, image/png"
                onChange={handleFileChange}
              />
              <button type="button" className="Button" data-brand={brand} onClick={handleFileClick}>
                {logoPreview ? "Change Logo" : "Upload Logo"}
              </button>
              <div className="text-xs text-gray-500">
                Supported formats: JPG, PNG. Max size: 2MB
              </div>
            </div>
          </div>
        </div>
        <div className="Modal__card__actions grid grid-cols-2">
          <button
            type="button"
            className="Button"
            data-brand={brand}
            onClick={onClose}
            disabled={uploadAndUpdateMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="Button"
            data-brand={brand}
            onClick={handleSave}
            disabled={uploadAndUpdateMutation.isLoading || !selectedFile}
          >
            {uploadAndUpdateMutation.isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
